import { Center, Stack, Text, VStack } from "@chakra-ui/react";
import React, { memo, useMemo } from "react";

import {
  isAudio,
  isImage,
  isOffice,
  isPdf,
  isText,
  isVideo,
} from "modules/utils/Functions";
import FileImage from "modules/files/file-image";
import FileOfficeViewer from "modules/files/file-office-viewer";
import FilePdfViewer from "modules/files/file-pdf-viewer";
import FileVideoPlayer from "modules/files/file-video-player";
import colors from "app/colors";
import {
  DownloadUrlQuery,
  DownloadUrlQueryVariables,
  FileGalleryFragment,
  MessageWithMessageThreadQuery,
  MessageWithMessageThreadQueryVariables,
} from "gql/graphql";
import FileGalleryHeader from "./file-gallery-header";
import FileTextViewer from "./file-text-viewer";
import { useQuery } from "@apollo/client";
import SliderFileButtons from "./slider-file-buttons";
import MESSAGE_WITH_MESSAGE_THREAD_QUERY from "modules/messaging/_networking/message-with-message-thread";
import {
  IMAGE_GALLERY_ITEM_MAX_HEIGHT,
  OFFICE_BASE_URL,
} from "modules/utils/Constants";
import DOWNLOAD_URL_QUERY from "modules/drawers/networking/download-url";
import FileThumb from "./file-thumb";

interface IProps {
  file: FileGalleryFragment;
  allowDelete: boolean;
  isActive: boolean;
}

const SliderFile = ({ file, allowDelete, isActive }: IProps) => {
  const { data } = useQuery<
    MessageWithMessageThreadQuery,
    MessageWithMessageThreadQueryVariables
  >(MESSAGE_WITH_MESSAGE_THREAD_QUERY, {
    variables: {
      id: file.messageId!,
    },
    skip: !file.messageId,
  });

  const { data: downloadUrlData } = useQuery<
    DownloadUrlQuery,
    DownloadUrlQueryVariables
  >(DOWNLOAD_URL_QUERY, {
    variables: {
      fileId: file.id,
      inline: true,
    },
    skip: !isOffice(file.contentType),
  });

  const fileUrl = useMemo(
    () =>
      downloadUrlData?.downloadUrl
        ? `${OFFICE_BASE_URL}${encodeURIComponent(downloadUrlData?.downloadUrl)}`
        : null,

    [downloadUrlData?.downloadUrl],
  );

  const message = useMemo(() => data?.messages[0], [data]);
  const messageThread = useMemo(() => data?.messages[0]?.messageThread, [data]);

  if (isImage(file.contentType)) {
    return (
      <Stack w="full" h="full" alignItems="center">
        <FileGalleryHeader message={message} file={file} />
        <FileImage file={file} />
        <SliderFileButtons
          file={file}
          messageThread={messageThread}
          message={message}
          allowDelete={allowDelete}
        />
      </Stack>
    );
  }

  if (isOffice(file.contentType)) {
    return (
      <Stack w="full" h="full" alignItems="center">
        <FileGalleryHeader message={message} file={file} />
        <FileOfficeViewer fileUrl={fileUrl} name={file.name} />
        <SliderFileButtons
          file={file}
          messageThread={messageThread}
          message={message}
          allowDelete={allowDelete}
        />
      </Stack>
    );
  }

  if (isPdf(file.contentType)) {
    return (
      <Stack w="full" h="full" alignItems="center">
        <FileGalleryHeader message={message} file={file} />
        <FilePdfViewer fileUrl={file.cloudFrontUrl} name={file.name} />
        <SliderFileButtons
          file={file}
          messageThread={messageThread}
          message={message}
          allowDelete={allowDelete}
        />
      </Stack>
    );
  }

  if (isVideo(file.contentType)) {
    return (
      <Stack w="full" h="full" alignItems="center">
        <FileGalleryHeader message={message} file={file} />
        <FileVideoPlayer
          isAudio={isAudio(file.contentType)}
          fileUrl={file.cloudFrontUrl}
          isActive={isActive}
        />
        <SliderFileButtons
          file={file}
          messageThread={messageThread}
          message={message}
          allowDelete={allowDelete}
        />
      </Stack>
    );
  }

  if (isText(file.contentType)) {
    return (
      <Stack w="full" h="full" alignItems="center">
        <FileGalleryHeader message={message} file={file} />
        <FileTextViewer fileUrl={file.cloudFrontUrl} />
        <SliderFileButtons
          file={file}
          messageThread={messageThread}
          message={message}
          allowDelete={allowDelete}
        />
      </Stack>
    );
  }

  return (
    <Stack w="full" h="full" alignItems="center">
      <FileGalleryHeader message={message} file={file} />
      <Center
        display="flex"
        flexDirection="column"
        w="calc(100vw - 160px)"
        h="full"
        maxH={IMAGE_GALLERY_ITEM_MAX_HEIGHT}
      >
        <VStack spacing={2}>
          <FileThumb
            contentType={file.contentType}
            name={file.name}
            url={file.previewCloudFrontUrl}
            size={[179, 135]}
          />
          <Text
            pt={2}
            fontFamily="Outfit"
            fontSize={24}
            fontWeight={700}
            color={colors.white}
          >
            Preview Unavailable
          </Text>
          <Text fontSize={14} fontWeight={600} color={colors.gray30}>
            This file type cannot be shown as a preview.
          </Text>
        </VStack>
      </Center>
      <SliderFileButtons
        file={file}
        messageThread={messageThread}
        message={message}
        allowDelete={allowDelete}
      />
    </Stack>
  );
};

export default memo(SliderFile);
